import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  ajvResolver,
  BaseellaStatus,
  BaseTransaction,
  Currency,
  Filters,
  PaymentType,
  ResponseError,
  responseErrorsResolver,
  SchemaFilterType,
  SelectItemProps,
  useFilters,
  useGetList,
  usePrevious,
  useQueryState,
} from '@ui/src';
import { useAppDispatch } from '@store';
import isEqual from 'lodash.isequal';
import {
  FintracReportFormValues,
  PaymentType as PaymentTypeProps,
} from '../types';
import {
  useLazyGetFintracIncomingTransactionsQuery,
  useCreateFintracReportMutation,
  useAddTransactionsToFintracReportMutation,
  useLazyGetFintracReportsQuery,
} from '../store/services';
import useGetAccountsAutocompleteList from './use-accounts-autocomplete';
import { filtersSchema, schema } from '../variables/incoming-transactions';
import formatIncomingList from '../helpers/fornat-incoming-transactions';
import { clearFintracTranscationsState } from '../store/slices/list';

import ROUTES from '../../../routes';

const scheme = {
  type: 'object',
  properties: {
    aggregationType: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Aggregation Type is required',
    },
    periodStart: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Transfer Date is required',
    },
  },
  required: ['aggregationType', 'periodStart'],
};

const useCreateEFTR = ({
  transactions,
  currencies,
  type,
  paymentTypes,
}: {
  transactions?: BaseTransaction[];
  currencies?: Currency[];
  type: 'eftr' | 'lvctr';
  paymentTypes?: PaymentTypeProps[];
}) => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [selectedTransactionsIds, setSelectedTransactionsIds] = useState<
    string[]
  >([]);
  const [transactionsTableVisible, setTranscationsTableVisible] =
    useState(false);
  const [availablePaymentTypes, setAvailablePaymentTypes] = useState<
    PaymentTypeProps[]
  >([]);

  const [createdReportId, setCreatedReportId] = useState('');

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { search } = useLocation();

  const [getTransactionsList, { isLoading, isFetching }] =
    useLazyGetFintracIncomingTransactionsQuery();
  const [
    createReport,
    { isLoading: createReportInProgress, isSuccess: createReportsIsSuccess },
  ] = useCreateFintracReportMutation();
  const [
    addTransactionsToReport,
    {
      isLoading: addTransactionsInProgress,
      isSuccess: addTransactionsIsSuccess,
    },
  ] = useAddTransactionsToFintracReportMutation();
  const [getFintracReport] = useLazyGetFintracReportsQuery();

  const { filters, sort, onSortChange, onFilterChange } = useQueryState(
    schema,
    true,
  );

  const {
    autocompleteList: recipientAccounts,
    onSearch: onSearchRecipientAccounts,
    onClear,
  } = useGetAccountsAutocompleteList(
    '&isClientAccount=true&sortProp=fullNumber&sortDir=asc',
    'fullNumber',
  );

  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
    setError,
  } = useForm<FintracReportFormValues>({
    resolver: ajvResolver(scheme),
  });

  const transferDate = watch('periodStart');

  const prevTransferDate = usePrevious(transferDate);

  const onRowSelect = (ids: string[]) => {
    setSelectedTransactionsIds(ids);
  };

  const formattedData = useMemo(() => {
    const data = formatIncomingList({
      list: transactions as BaseTransaction[],
      currenciesList: currencies,
      paymentTypes,
    });

    return data;
  }, [transactions, currencies, paymentTypes]);

  const { getListAction, setPage, onFilter, onSort, errorList } =
    useGetList<BaseTransaction>({
      getList: getTransactionsList,
      onFilterChange,
      onSortChange,
      schema,
      withoutChangeSearchParams: true,
    });

  const onSearchAutocomplete = (name: string, searchAutocomplete: string) => {
    if (name === 'recipientAccountNumber') {
      onSearchRecipientAccounts(searchAutocomplete);
    }
  };

  const {
    filtersComponent,
    setValues,
    handleSubmit: tableHandleSubmit,
  } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: onFilter,
    onSearchAutocomplete,
  });

  const onTableSetPage = ({
    id,
    take,
    filters: filtersValues,
  }: {
    id: string;
    take: string;
    filters: Filters;
  }) => {
    const dateFrom = new Date(transferDate);
    const dateTo = new Date(transferDate);

    dateFrom.setHours(0);
    dateFrom.setMinutes(0);
    dateFrom.setSeconds(0);
    dateTo.setHours(23);
    dateTo.setMinutes(59);
    dateTo.setSeconds(59);

    const nextFilters: Filters = {
      ...filtersValues,
      baseellaStatus: {
        value: [BaseellaStatus.ARCH],
        type: SchemaFilterType.STRING,
      },
      transferDateFrom: {
        value: [dateFrom.toISOString()],
        type: SchemaFilterType.STRING,
      },
      transferDateTo: {
        value: [dateTo.toISOString()],
        type: SchemaFilterType.STRING,
      },
      paymentType: {
        value: availablePaymentTypes?.map((item) => item.paymentType),
        type: SchemaFilterType.STRING,
      },
    };

    setPage({ id, take, filters: nextFilters });
  };

  const onTableFilter = (values: Filters) => {
    const dateFrom = new Date(transferDate);
    const dateTo = new Date(transferDate);

    dateFrom.setHours(0);
    dateFrom.setMinutes(0);
    dateFrom.setSeconds(0);
    dateTo.setHours(23);
    dateTo.setMinutes(59);
    dateTo.setSeconds(59);

    const filtersValues: Filters = {
      ...values,
      take: {
        value: ['30'],
        type: SchemaFilterType.STRING,
      },
      baseellaStatus: {
        value: [BaseellaStatus.ARCH],
        type: SchemaFilterType.STRING,
      },
      transferDateFrom: {
        value: [dateFrom.toISOString()],
        type: SchemaFilterType.STRING,
      },
      transferDateTo: {
        value: [dateTo.toISOString()],
        type: SchemaFilterType.STRING,
      },
      paymentType: {
        value: availablePaymentTypes?.map((item) => item.paymentType),
        type: SchemaFilterType.STRING,
      },
    };

    onFilter(filtersValues);
  };

  const getReportsList = async () => {
    await getFintracReport(
      `${search}${search ? '&' : '?'}reportType=${type === 'eftr' ? 'EFTR' : 'LVCTR'}&sortDir=desc&sortProp=createdAt`,
    );
  };

  const handleClose = () => {
    setPanelIsOpen(false);
    setTranscationsTableVisible(false);
    setSelectedTransactionsIds([]);
    dispatch(clearFintracTranscationsState());
    setCreatedReportId('');

    setTimeout(() => {
      navigate(
        `${type === 'eftr' ? ROUTES.FINTRAC_EFTR : ROUTES.FINTRAC_LVCTR}${search || ''}`,
      );
    }, 100);
  };

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    const dateFrom = new Date(values?.periodStart);
    const dateTo = new Date(values?.periodStart);

    dateFrom.setHours(0);
    dateFrom.setMinutes(0);
    dateFrom.setSeconds(0);
    dateTo.setHours(23);
    dateTo.setMinutes(59);
    dateTo.setSeconds(59);

    let response: any;

    if (!createdReportId) {
      response = (await createReport({
        aggregationType: values.aggregationType,
        reportType: type === 'eftr' ? 'EFTR' : 'LVCTR',
        periodStart: dateFrom?.toISOString(),
        periodEnd: dateTo?.toISOString(),
      })) as { error?: ResponseError; data?: { data?: { id: string } } };
    }

    if (response?.error) {
      responseErrorsResolver({
        errors: response?.error,
        setError,
        setGlobalError,
      });
    } else if (!response?.error || createdReportId) {
      setCreatedReportId(response?.data?.data?.id as string);

      if (selectedTransactionsIds?.length) {
        const transactionsList: {
          regulatoryReportId: string;
          transactionId: string;
        }[] = [];

        selectedTransactionsIds?.forEach((item) => {
          transactionsList.push({
            regulatoryReportId: createdReportId || response?.data?.data?.id,
            transactionId: item,
          });
        });

        const addTransactionsResponse = (await addTransactionsToReport({
          items: transactionsList,
        })) as { error?: ResponseError };

        if (addTransactionsResponse?.error) {
          responseErrorsResolver({
            errors: addTransactionsResponse?.error,
            setError,
            setGlobalError,
          });
        } else {
          handleClose();
        }
      } else {
        handleClose();
      }
    }
  });

  useEffect(() => {
    if (prevTransferDate && transferDate && prevTransferDate !== transferDate) {
      onTableFilter(filters);
    }
  }, [prevTransferDate, transferDate]);

  useEffect(() => {
    if (paymentTypes) {
      setAvailablePaymentTypes(
        type === 'eftr'
          ? paymentTypes?.filter(
              (item) => item.paymentType !== PaymentType.CRYPTO_ASSET_PAYMENT,
            )
          : paymentTypes?.filter(
              (item) => item.paymentType === PaymentType.CRYPTO_ASSET_PAYMENT,
            ),
      );
    }
  }, [paymentTypes, type]);

  useEffect(() => {
    if (transactions) {
      if (
        !filters?.cursor?.value?.length &&
        (!selectedTransactionsIds?.length ||
          !isEqual(
            transactions?.map((item) => item.id),
            selectedTransactionsIds,
          )) &&
        transactions?.length
      ) {
        setSelectedTransactionsIds(transactions?.map((item) => item.id));
      } else if (filters?.cursor?.value?.length) {
        const nextSelectedTransactionsIds = ([] as string[]).concat(
          selectedTransactionsIds,
        );

        transactions?.forEach((item) => {
          if (!nextSelectedTransactionsIds?.find((id) => item.id === id)) {
            nextSelectedTransactionsIds.push(item.id);
          }
        });

        setSelectedTransactionsIds(nextSelectedTransactionsIds);
      }
    }
  }, [transactions, filters]);

  useEffect(() => {
    if (panelIsOpen) {
      setValues('recipientAccountNumber', recipientAccounts || []);
      setValues(
        'currencyId',
        currencies?.map((item) => ({
          name: item.name,
          value: item.id,
        })) as SelectItemProps[],
      );
    }
  }, [recipientAccounts, currencies]);

  useEffect(() => {
    setPanelIsOpen(true);
  }, []);

  useEffect(() => {
    if (transferDate && availablePaymentTypes) {
      const dateFrom = new Date(transferDate);
      const dateTo = new Date(transferDate);

      dateFrom.setHours(0);
      dateFrom.setMinutes(0);
      dateFrom.setSeconds(0);
      dateTo.setHours(23);
      dateTo.setMinutes(59);
      dateTo.setSeconds(59);

      const filtersValues: any = {
        take: {
          value: ['30'],
          type: SchemaFilterType.STRING,
        },
        baseellaStatus: {
          value: [BaseellaStatus.ARCH],
          type: SchemaFilterType.STRING,
        },
        transferDateFrom: {
          value: [dateFrom.toISOString()],
          type: SchemaFilterType.STRING,
        },
        transferDateTo: {
          value: [dateTo.toISOString()],
          type: SchemaFilterType.STRING,
        },
        paymentType: {
          value: availablePaymentTypes?.map((item) => item.paymentType),
          type: SchemaFilterType.STRING,
        },
      };

      getListAction({
        filters: filtersValues,
        sort,
      });

      setTranscationsTableVisible(true);
    }
  }, [availablePaymentTypes, transferDate]);

  useEffect(() => {
    if (createReportsIsSuccess) {
      getReportsList();
    }
  }, [createReportsIsSuccess]);

  useEffect(() => {
    if (addTransactionsIsSuccess) {
      setTimeout(() => {
        getReportsList();
      }, 1000);
    }
  }, [addTransactionsIsSuccess]);

  return {
    panelIsOpen,
    handleClose,
    onSubmit,
    globalError,
    control,
    errors,
    onRowSelect,
    formattedData,
    transactionsTableVisible,
    isLoading,
    isFetching,
    filtersComponent,
    tableHandleSubmit,
    onClear,
    onTableSetPage,
    onSort,
    errorList,
    onTableFilter,
    sort,
    filters,
    selectedTransactionsIds,
    createReportInProgress,
    addTransactionsInProgress,
  };
};

export default useCreateEFTR;
